import React, { useEffect, useState } from "react";
import { Sparkles, Upload, Loader2, Check, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { money, PageHeader, Empty } from "@/components/shared";
import { SelectBox } from "@/pages/Clients";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function AIImport() {
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [ings, setIngs] = useState([]);
  const [file, setFile] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/brands").then((r) => { setBrands(r.data); setBrandId(r.data[0]?.id || ""); });
    api.get("/ingredients").then((r) => setIngs(r.data));
  }, []);

  const analyze = async () => {
    if (!file.length || !brandId) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("brand_id", brandId);
      file.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/ai-import/analyze", fd);
      setResult(data);
      toast.success(`Extracted ${data.items.length} menu item(s)`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setItem = (i, patch) => setResult((r) => ({ ...r, items: r.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) }));
  const setLine = (i, li, patch) => setResult((r) => ({
    ...r, items: r.items.map((it, idx) => idx !== i ? it : { ...it, recipe: it.recipe.map((l, lx) => lx === li ? { ...l, ...patch } : l) }),
  }));

  const commit = async () => {
    setBusy(true);
    try {
      const items = result.items.filter((it) => it.approved).map((it) => ({
        name: it.name, sku: it.sku, category: it.category, selling_price: Number(it.selling_price) || 0,
        brand_id: it.brand_id,
        recipe: it.recipe.filter((l) => l.approved).map((l) => {
          const useId = l.matched_ingredient_id && l.matched_ingredient_id !== "__new__";
          return useId
            ? { ingredient_id: l.matched_ingredient_id, name: l.extracted_name, qty: Number(l.qty), unit: l.unit }
            : { ingredient_id: null, name: l.extracted_name, qty: Number(l.qty), unit: l.unit, is_new: true };
        }),
      }));
      const { data } = await api.post("/ai-import/commit", { brand_id: result.brand_id, items });
      toast.success(`Imported ${data.created_menu_items} items · ${data.created_ingredients} new ingredients`);
      setResult(null); setFile([]);
      api.get("/ingredients").then((r) => setIngs(r.data));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const ingOpts = [{ value: "__new__", label: "➕ Create as new ingredient" }, ...ings.map((i) => ({ value: i.id, label: i.name }))];

  return (
    <div>
      <PageHeader title="AI Menu & Recipe Import" subtitle="Upload Excel, CSV or PDF — AI extracts items, recipes and ingredients for review" />

      <div className="card-surface p-5 mb-6">
        <div className="grid sm:grid-cols-3 gap-3 items-end">
          <div>
            <label className="text-xs text-muted-foreground">Target brand</label>
            <div className="mt-1.5"><SelectBox value={brandId} onChange={setBrandId} options={brands.map((b) => ({ value: b.id, label: b.name }))} testid="import-brand" /></div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">File (.xlsx, .csv, .pdf)</label>
            <Input type="file" multiple accept=".xlsx,.xls,.csv,.pdf" onChange={(e) => setFile([...e.target.files])} className="mt-1.5" data-testid="import-file" />
          </div>
          <Button onClick={analyze} disabled={busy || !file.length} data-testid="analyze-btn" className="gap-1.5 bg-primary text-primary-foreground hover:bg-amber-400 font-semibold">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Analyze with AI
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-3">Upload one or several files (multiple brand menus supported). Nothing is saved until you review and confirm below.</p>
      </div>

      {result && (
        <>
          {result.unreadable?.length > 0 && (
            <div className="card-surface p-4 mb-4 border-orange-500/40">
              <p className="text-sm font-semibold text-orange-400 flex items-center gap-1.5 mb-1"><AlertTriangle className="h-4 w-4" /> Needs manual review</p>
              <ul className="text-sm text-slate-300 list-disc pl-5">{result.unreadable.map((u, i) => <li key={i}>{u}</li>)}</ul>
            </div>
          )}
          {result.items.length === 0 ? <Empty icon={Sparkles} title="No menu items extracted" /> : (
            <div className="space-y-4">
              {result.items.map((it, i) => (
                <div key={i} className={`card-surface p-4 ${it.approved ? "" : "opacity-50"} ${it.needs_review ? "border-orange-500/40" : ""}`} data-testid={`import-item-${i}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <input type="checkbox" checked={it.approved} onChange={(e) => setItem(i, { approved: e.target.checked })} data-testid={`item-approve-${i}`} className="h-4 w-4" />
                    <Input value={it.name} onChange={(e) => setItem(i, { name: e.target.value })} className="flex-1 font-semibold" data-testid={`item-name-${i}`} />
                    <Input value={it.category} onChange={(e) => setItem(i, { category: e.target.value })} className="w-32" placeholder="Category" />
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-muted-foreground">AED</span>
                      <Input type="number" value={it.selling_price} onChange={(e) => setItem(i, { selling_price: e.target.value })} className="w-24" />
                    </div>
                    <div className="w-40"><SelectBox value={it.brand_id} onChange={(v) => setItem(i, { brand_id: v })} options={brands.map((b) => ({ value: b.id, label: b.name }))} /></div>
                    {it.source && <span className="text-[10px] text-muted-foreground font-mono">{it.source}</span>}
                    {it.needs_review && <span className="text-[10px] font-bold text-orange-400 border border-orange-500/40 rounded px-1.5 py-0.5">REVIEW</span>}
                  </div>
                  <div className="space-y-2 pl-7">
                    {it.recipe.map((l, li) => (
                      <div key={li} className="flex items-center gap-2 flex-wrap">
                        <input type="checkbox" checked={l.approved} onChange={(e) => setLine(i, li, { approved: e.target.checked })} className="h-3.5 w-3.5" />
                        <Input value={l.extracted_name} onChange={(e) => setLine(i, li, { extracted_name: e.target.value })} className="w-44" data-testid={`line-name-${i}-${li}`} />
                        <Input type="number" value={l.qty} onChange={(e) => setLine(i, li, { qty: e.target.value })} className="w-20" />
                        <Input value={l.unit} onChange={(e) => setLine(i, li, { unit: e.target.value })} className="w-16" />
                        <div className="w-56"><SelectBox value={l.matched_ingredient_id || "__new__"} onChange={(v) => setLine(i, li, { matched_ingredient_id: v, is_new: v === "__new__" })} options={ingOpts} /></div>
                        {l.is_new ? <span className="text-[10px] text-emerald-400">NEW</span> : <span className="text-[10px] text-sky-400">match {l.match_score}%</span>}
                        {l.needs_review && <span className="text-[10px] text-orange-400">check</span>}
                      </div>
                    ))}
                    {it.recipe.length === 0 && <p className="text-sm text-orange-400">No recipe extracted — add ingredients or reject.</p>}
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-end gap-3 sticky bottom-4">
                <Button variant="outline" onClick={() => setResult(null)}>Discard</Button>
                <Button onClick={commit} disabled={busy} data-testid="commit-import-btn" className="gap-1.5 bg-primary text-primary-foreground hover:bg-amber-400 font-semibold">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Confirm Import
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
